// Painel de atendimentos: hoje, próximos 7 dias e números do mês
import { useEffect, useMemo, useState } from "react";
import { Calendar, CheckCircle2, Clock, Loader2, MapPin, Phone, RefreshCw, UserCheck, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  status: string;
  conversion_status?: string | null;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  meeting_subject?: string | null;
  location?: string | null;
  urgency_level?: string | null;
}

interface AppointmentDashboardProps {
  className?: string;
}

const norm = (v?: string | null) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const isDone = (a: Appointment) => norm(a.status).startsWith("conclu") || norm(a.status) === "realizado";
const isCanceled = (a: Appointment) => norm(a.status).startsWith("cancel");
const isConverted = (a: Appointment) => ["convertido", "converted", "cliente"].includes(norm(a.conversion_status));

const STATUS_TONE: Record<string, string> = {
  agendado: "bg-blue-50 text-blue-700",
  confirmado: "bg-emerald-50 text-emerald-700",
  concluido: "bg-slate-100 text-slate-600",
  cancelado: "bg-red-50 text-red-600",
  reagendado: "bg-amber-50 text-amber-700",
};

const StatusPill = ({ status }: { status: string }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_TONE[norm(status)] || "bg-slate-100 text-slate-600"}`}>
    {status}
  </span>
);

const DateBox = ({ iso }: { iso: string }) => {
  const d = new Date(iso);
  return (
    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl border border-[#c4ab83]/30 bg-[#faf7f2]">
      <span className="text-[10px] font-semibold uppercase leading-none text-[#9a7f52]">{format(d, "MMM", { locale: ptBR }).replace(".", "")}</span>
      <span className="text-lg font-semibold leading-tight tabular-nums text-slate-900">{format(d, "dd")}</span>
    </div>
  );
};

export const AppointmentDashboard: React.FC<AppointmentDashboardProps> = ({ className }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from("appointments")
        .select("id, title, appointment_date, status, conversion_status, prospect_name, prospect_phone, meeting_subject, location, urgency_level")
        .eq("user_id", user.id)
        .order("appointment_date", { ascending: true });
      if (error) throw error;
      setAppointments((data as Appointment[]) || []);
    } catch (error) {
      console.error("Erro ao buscar atendimentos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const { today, upcoming, month } = useMemo(() => {
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endToday = new Date(startToday.getTime() + 86400000);
    const endWeek = new Date(startToday.getTime() + 8 * 86400000);
    const active = appointments.filter((a) => !isCanceled(a));
    const today = active.filter((a) => {
      const d = new Date(a.appointment_date);
      return d >= startToday && d < endToday;
    });
    const upcoming = active
      .filter((a) => {
        const d = new Date(a.appointment_date);
        return d >= endToday && d < endWeek && !isDone(a);
      })
      .slice(0, 6);
    const inMonth = appointments.filter((a) => {
      const d = new Date(a.appointment_date);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    const done = inMonth.filter(isDone).length;
    const converted = inMonth.filter(isConverted).length;
    const pending = inMonth.filter((a) => !isDone(a) && !isCanceled(a)).length;
    return {
      today,
      upcoming,
      month: {
        total: inMonth.filter((a) => !isCanceled(a)).length,
        done,
        converted,
        pending,
        rate: done > 0 ? Math.round((converted / done) * 100) : 0,
      },
    };
  }, [appointments]);

  if (loading) {
    return (
      <div className={`flex justify-center py-16 ${className || ""}`}>
        <Loader2 className="h-6 w-6 animate-spin text-[#c4ab83]" />
      </div>
    );
  }

  const name = (a: Appointment) => a.prospect_name || a.title;

  return (
    <div className={`grid grid-cols-1 gap-5 lg:grid-cols-3 ${className || ""}`}>
      <div className="space-y-5 lg:col-span-2">
        {/* Hoje */}
        <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Hoje</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">
                {today.length ? `${today.length} atendimento${today.length === 1 ? "" : "s"}` : "Agenda livre"}
              </p>
            </div>
            <span className="text-xs capitalize text-slate-400">{format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}</span>
          </div>
          {today.length === 0 ? (
            <div className="py-10 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#faf7f2]">
                <Calendar className="h-5 w-5 text-[#c4ab83]" />
              </div>
              <p className="mt-3 text-sm text-slate-500">Nenhum atendimento agendado para hoje.</p>
            </div>
          ) : (
            <ul className="mt-4 space-y-2">
              {today.map((a) => (
                <li key={a.id} className="flex items-start gap-4 rounded-xl border border-slate-100 p-3 transition-colors hover:border-[#c4ab83]/40 hover:bg-[#faf7f2]/50">
                  <div className="w-14 shrink-0 text-center">
                    <p className="text-lg font-semibold tabular-nums text-slate-900">{format(new Date(a.appointment_date), "HH:mm")}</p>
                  </div>
                  <div className="min-w-0 flex-1 border-l border-slate-100 pl-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium text-slate-900">{name(a)}</p>
                      <StatusPill status={a.status} />
                    </div>
                    {a.meeting_subject && <p className="mt-0.5 truncate text-sm text-slate-500">{a.meeting_subject}</p>}
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                      {a.prospect_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.prospect_phone}</span>}
                      {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Próximos 7 dias */}
        <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Próximos 7 dias</p>
          <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Próximos atendimentos</p>
          {upcoming.length === 0 ? (
            <div className="py-10 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#faf7f2]">
                <Clock className="h-5 w-5 text-[#c4ab83]" />
              </div>
              <p className="mt-3 text-sm text-slate-500">Nenhum atendimento nos próximos dias.</p>
            </div>
          ) : (
            <ul className="mt-4 space-y-1">
              {upcoming.map((a) => {
                const d = new Date(a.appointment_date);
                const when = isToday(d) ? "Hoje" : isTomorrow(d) ? "Amanhã" : format(d, "EEEE", { locale: ptBR });
                return (
                  <li key={a.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#faf7f2]/70">
                    <DateBox iso={a.appointment_date} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{name(a)}</p>
                      <p className="truncate text-xs capitalize text-slate-500">
                        {when} · {format(d, "HH:mm")}
                        {a.meeting_subject ? ` · ${a.meeting_subject}` : ""}
                      </p>
                    </div>
                    <StatusPill status={a.status} />
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* Números do mês */}
      <section className="h-fit overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Este mês</p>
            <p className="mt-0.5 text-lg font-semibold capitalize tracking-tight text-slate-900">{format(new Date(), "MMMM", { locale: ptBR })}</p>
          </div>
          <button type="button" onClick={fetchDashboardData} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-[#faf7f2] hover:text-[#7a6440]" title="Atualizar">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-px bg-slate-100">
          {[
            { label: "Agendados", value: month.total, icon: Users },
            { label: "Concluídos", value: month.done, icon: CheckCircle2 },
            { label: "Viraram clientes", value: month.converted, icon: UserCheck },
            { label: "Pendentes", value: month.pending, icon: Clock },
          ].map((tile) => (
            <div key={tile.label} className="bg-white px-5 py-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
                <tile.icon className="h-3.5 w-3.5 text-[#c4ab83]" />
              </div>
              <p className={`mt-1 text-3xl font-semibold tabular-nums tracking-tight ${tile.value ? "text-slate-900" : "text-slate-300"}`}>{tile.value}</p>
            </div>
          ))}
        </div>
        <div className="px-5 py-4">
          <div className="flex items-baseline justify-between">
            <p className="text-sm text-slate-500">Taxa de conversão</p>
            <p className={`text-2xl font-semibold tabular-nums ${month.rate ? "text-[#7a6440]" : "text-slate-300"}`}>{month.rate}%</p>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gradient-to-r from-[#c4ab83] to-[#e8d5a8] transition-all duration-700" style={{ width: `${month.rate}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Atendimentos concluídos que viraram clientes.</p>
        </div>
      </section>
    </div>
  );
};
